import { useToast } from '@/hooks/useToast';
import { getBaseUrl } from '@/services/api';
import { getAuthToken } from '@/services/auth/getAuthToken';
import { downloadFromSource } from '@/utils/downloadUtils';
import { useCallback, useEffect, useRef, useState } from 'react';

/** A message the server wrote for the user, as opposed to a network failure. */
class DownloadRefused extends Error {}

/** 4xx only: a 5xx message is ours, not written for a user, and should not reach a toast. */
async function serverExplanation(response: Response): Promise<string | undefined> {
  if (response.status < 400 || response.status >= 500) return undefined;
  try {
    const body: unknown = await response.json();
    const message = (body as { message?: unknown } | undefined)?.message;
    return typeof message === 'string' && message.length > 0 ? message : undefined;
  } catch {
    return undefined;
  }
}

interface FileDownloadOptions {
  filename: string;
  errorMessage?: string;
  method?: 'GET' | 'POST';
  body?: object;
}

export const useFileDownload = () => {
  const { showToast } = useToast();
  const abortControllerReference = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    return () => {
      abortControllerReference.current?.abort();
    };
  }, []);

  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(
    async (path: string, options: FileDownloadOptions) => {
      setIsDownloading(true);

      const abortController = new AbortController();
      abortControllerReference.current = abortController;

      const method = options.method ?? 'GET';
      const headers: Record<string, string> = {
        Authorization: `Bearer ${await getAuthToken()}`,
      };
      if (options.body !== undefined) {
        headers['Content-Type'] = 'application/json';
      }

      try {
        const response = await fetch(`${getBaseUrl()}${path}`, {
          method,
          headers,
          signal: abortController.signal,
          body: options.body === undefined ? undefined : JSON.stringify(options.body),
        });

        if (response.status === 401) {
          localStorage.removeItem('authToken');
          globalThis.location.reload();
          return;
        }

        if (!response.ok) {
          const explanation = await serverExplanation(response);
          if (explanation !== undefined) throw new DownloadRefused(explanation);
          throw new Error(`Download failed with status ${response.status}`);
        }

        await downloadFromSource(response, { filename: options.filename });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        showToast({
          message:
            error instanceof DownloadRefused ? error.message : (options.errorMessage ?? 'Failed to download file'),
          type: 'error',
        });
      } finally {
        abortControllerReference.current = undefined;
        setIsDownloading(false);
      }
    },
    [showToast]
  );

  return { download, isDownloading };
};
